/**
 * T3/T4/T6 — o teste DISC ou QA pelo link individual, SEM login, pensado para o celular.
 *
 * Mesmo padrão das páginas públicas da casa (`OsPublicaPage`, `FormCaptacaoPage`): fundo `paper`, `Card` central
 * com o `<Logo>` no topo, sem `AppShell`. Todos os textos são LITERAIS do conteúdo aprovado.
 *
 * Mecânica: abertura com aceite → uma pergunta por tela (salva no SERVIDOR na hora; fechar e reabrir continua de
 * onde parou; dá para voltar e mudar) → "Enviar respostas" com confirmação → o resultado. 🔴 Nenhuma letra, ponto ou
 * perfil chega aqui antes do envio: a tela recebe só as palavras (com id opaco) e o resultado vem pronto do servidor.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { Alert, Button, Card, Checkbox, useConfirmDialog } from '@/components/ui';
import { ResultadoTeste } from '@/components/testes/ResultadoTeste';
import { formatDate } from '@/lib/format-date';
import { testePublico } from '@/lib/testes/testes-store';
import { ABERTURA, INSTRUCAO, nomeDoTeste, preencher, TEXTOS, TOTAL } from '@/lib/testes/textos';
import type { EstadoPublico, PerguntaPublica, TipoTeste } from '@/lib/testes/tipos';
import { cn } from '@/lib/cn';

const LP = TEXTOS.link_publico;

type RespostaDisc = { mais?: string; menos?: string };

/** A moldura de toda tela pública dos testes: logo, o nome do teste e o conteúdo num `Card`. */
function Moldura({ titulo, children }: { titulo?: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-paper px-3 py-6 dark:bg-paper-dark md:px-4 md:py-10">
      <div className="mx-auto max-w-xl">
        <Card className="overflow-hidden border-ink-1/10 bg-paper dark:border-ink-dark-1/10 dark:bg-paper-dark">
          <div className="border-b border-ink-1/10 p-5 dark:border-ink-dark-1/10 md:p-6">
            <Logo size="md" className="h-10 md:h-12" />
            {titulo && <p className="mt-2 text-xs font-semibold uppercase tracking-[0.1em] text-ink-3">{titulo}</p>}
          </div>
          <div className="p-5 md:p-6">{children}</div>
        </Card>
      </div>
    </div>
  );
}

function Mensagem({ texto }: { texto: string }) {
  return (
    <Moldura>
      <p className="text-base text-ink-1 dark:text-ink-dark-1" data-testid="teste-mensagem">
        {texto}
      </p>
    </Moldura>
  );
}

export function TestePublicoPage() {
  const { token = '' } = useParams<{ token: string }>();
  const confirmar = useConfirmDialog();
  const [estado, setEstado] = useState<EstadoPublico | null>(null);
  const [falha, setFalha] = useState(false);
  const [aceito, setAceito] = useState(false);
  const [indice, setIndice] = useState(0);
  const [respostas, setRespostas] = useState<Record<number, unknown>>({});
  const [falta, setFalta] = useState(false);
  const [erroAoSalvar, setErroAoSalvar] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const aplicar = useCallback((e: EstadoPublico) => {
    setEstado(e);
    if (e.estado === 'abertura' || e.estado === 'respondendo') {
      const r = Object.fromEntries(Object.entries(e.respostas).map(([k, v]) => [Number(k), v]));
      setRespostas(r);
      // Reabrir vai direto para a primeira pergunta sem resposta.
      const primeiraVazia = e.perguntas.findIndex((_, i) => r[i] === undefined);
      setIndice(primeiraVazia < 0 ? e.perguntas.length - 1 : primeiraVazia);
    }
  }, []);

  useEffect(() => {
    testePublico.estado(token).then(aplicar).catch(() => setFalha(true));
  }, [token, aplicar]);

  const perguntas = useMemo(() => (estado && 'perguntas' in estado ? estado.perguntas : []), [estado]);

  if (falha) return <Mensagem texto={LP.encerrado} />;
  if (!estado) {
    return (
      <Moldura>
        <div className="h-24 animate-pulse rounded bg-paper-warm dark:bg-paper-dark-warm" aria-busy="true" />
      </Moldura>
    );
  }
  if (!('tipo' in estado)) {
    const frase = { vencido: LP.vencido, encerrado: LP.encerrado, indisponivel: LP.indisponivel_sistema, muitas_tentativas: LP.muitas_tentativas };
    return <Mensagem texto={frase[estado.estado]} />;
  }

  // ── Resultado ────────────────────────────────────────────────────────────
  if (estado.estado === 'resultado') {
    const { testeMinusculo } = nomeDoTeste(estado.tipo);
    return (
      <Moldura titulo={LP.titulo_resultado}>
        <div className="space-y-6">
          {estado.reaberto ? (
            <div>
              <h1 className="font-display text-2xl font-medium text-ink-1 dark:text-ink-dark-1">{preencher(LP.reaberto_titulo, { teste_minusculo: testeMinusculo })}</h1>
              <p className="text-sm text-ink-2 dark:text-ink-dark-2">{preencher(LP.reaberto_intro, { data: formatDate(estado.concluidoEmIso) })}</p>
            </div>
          ) : (
            <h1 className="font-display text-2xl font-medium text-ink-1 dark:text-ink-dark-1" data-testid="teste-concluido">
              {preencher(LP.concluido, { primeiro_nome: estado.primeiroNome })}
            </h1>
          )}
          <ResultadoTeste visao={estado.visao} />
          <Button size="lg" className="w-full md:w-auto" onClick={() => window.location.assign(testePublico.caminhoDoPdf(token))} data-testid="teste-pdf">
            {LP.botao_pdf}
          </Button>
        </div>
      </Moldura>
    );
  }

  const tipo: TipoTeste = estado.tipo;
  const { teste } = nomeDoTeste(tipo);

  // ── Abertura ─────────────────────────────────────────────────────────────
  if (estado.estado === 'abertura') {
    const a = ABERTURA[tipo];
    const v = { primeiro_nome: estado.primeiroNome, nome_completo: estado.nomeCompleto, data_validade: estado.dataValidade };
    return (
      <Moldura titulo={teste}>
        <div className="space-y-4 text-base leading-relaxed text-ink-1 dark:text-ink-dark-1" data-testid="teste-abertura">
          <h1 className="font-display text-2xl font-medium">{a.titulo}</h1>
          <p>{preencher(a.apresentacao, v)}</p>
          <p>{preencher(a.quem_ve, v)}</p>
          <Alert variant="neutro">{preencher(a.identidade, v)}</Alert>
          <Checkbox checked={aceito} onChange={(e) => setAceito(e.target.checked)} label={a.consentimento} data-testid="teste-aceite" />
          <p className="text-sm text-ink-2 dark:text-ink-dark-2">{preencher(a.salvamento, v)}</p>
          <Button
            size="lg"
            className="w-full"
            disabled={!aceito || ocupado}
            data-testid="teste-comecar"
            onClick={async () => {
              setOcupado(true);
              try {
                aplicar(await testePublico.aceitar(token));
              } catch {
                setErroAoSalvar(true);
              } finally {
                setOcupado(false);
              }
            }}
          >
            {a.botao}
          </Button>
          {erroAoSalvar && <Alert variant="danger">{LP.falha_ao_salvar}</Alert>}
        </div>
      </Moldura>
    );
  }

  // ── Uma pergunta por tela ────────────────────────────────────────────────
  const p = perguntas[indice] as PerguntaPublica;
  const resposta = respostas[indice];
  const respondida = tipo === 'disc' ? !!(resposta as RespostaDisc)?.mais && !!(resposta as RespostaDisc)?.menos : typeof resposta === 'number';
  const ultima = indice === perguntas.length - 1;

  async function guardar(valor: unknown) {
    setRespostas((r) => ({ ...r, [indice]: valor }));
    setFalta(false);
    const completo = tipo === 'disc' ? !!(valor as RespostaDisc).mais && !!(valor as RespostaDisc).menos : true;
    if (!completo) return;
    try {
      const r = await testePublico.responder(token, indice, valor);
      if ('estado' in r) aplicar(r as EstadoPublico);
      setErroAoSalvar(false);
    } catch {
      setErroAoSalvar(true);
    }
  }

  function marcarDisc(palavra: string, lado: 'mais' | 'menos') {
    const atual = (resposta as RespostaDisc) ?? {};
    const outro = lado === 'mais' ? 'menos' : 'mais';
    const novo: RespostaDisc = { ...atual, [lado]: palavra };
    if (novo[outro] === palavra) delete novo[outro];
    void guardar(novo);
  }

  async function avancar() {
    if (!respondida) {
      setFalta(true);
      return;
    }
    if (!ultima) {
      setIndice(indice + 1);
      return;
    }
    const tudo = perguntas.every((_, i) => respostas[i] !== undefined);
    if (!tudo) {
      setIndice(perguntas.findIndex((_, i) => respostas[i] === undefined));
      setFalta(true);
      return;
    }
    const ok = await confirmar({ title: LP.botao_enviar, message: LP.confirmar_envio, confirmLabel: LP.botao_enviar, cancelLabel: LP.botao_voltar });
    if (!ok) return;
    setOcupado(true);
    try {
      aplicar(await testePublico.enviar(token));
    } catch {
      setErroAoSalvar(true);
    } finally {
      setOcupado(false);
    }
  }

  const contador = preencher(tipo === 'disc' ? LP.contador_disc : LP.contador_qa, { n: indice + 1 });
  return (
    <Moldura titulo={teste}>
      <div className="space-y-5" data-testid="teste-pergunta">
        <div>
          <p className="text-sm font-semibold text-ink-2 dark:text-ink-dark-2" data-testid="teste-contador">{contador}</p>
          <div className="mt-2 h-1.5 w-full rounded-full bg-paper-warm dark:bg-paper-dark-warm" aria-hidden="true">
            <div className="h-1.5 rounded-full bg-red" style={{ width: `${((indice + 1) / TOTAL[tipo]) * 100}%` }} />
          </div>
        </div>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2">{INSTRUCAO[tipo]}</p>

        {p.tipo === 'disc' ? (
          <ul className="space-y-2">
            {p.palavras.map((w) => {
              const r = (resposta as RespostaDisc) ?? {};
              return (
                <li key={w.id} className="flex items-center justify-between gap-3 rounded-md border border-line px-3 py-2 dark:border-line-dark">
                  <span className="text-base font-medium text-ink-1 dark:text-ink-dark-1">{w.texto}</span>
                  <span className="flex shrink-0 gap-2">
                    <Button variant={r.mais === w.id ? 'primary' : 'outline'} className="min-h-[44px] min-w-[72px]" aria-pressed={r.mais === w.id} onClick={() => marcarDisc(w.id, 'mais')} data-testid={`mais-${w.id}`}>
                      {LP.botao_mais}
                    </Button>
                    <Button variant={r.menos === w.id ? 'primary' : 'outline'} className="min-h-[44px] min-w-[72px]" aria-pressed={r.menos === w.id} onClick={() => marcarDisc(w.id, 'menos')} data-testid={`menos-${w.id}`}>
                      {LP.botao_menos}
                    </Button>
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="space-y-3">
            <p className="text-base font-medium leading-relaxed text-ink-1 dark:text-ink-dark-1">{p.situacao}</p>
            <p className="text-base text-ink-1 dark:text-ink-dark-1">{p.pergunta}</p>
            <div className="grid grid-cols-5 gap-2" role="radiogroup">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={resposta === n}
                  onClick={() => void guardar(n)}
                  data-testid={`nota-${n}`}
                  className={cn(
                    'min-h-[48px] rounded-md border text-lg font-semibold transition-colors',
                    resposta === n ? 'border-red bg-red text-white' : 'border-line text-ink-1 hover:bg-paper-warm dark:border-line-dark dark:text-ink-dark-1 dark:hover:bg-paper-dark-warm',
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between gap-4 text-xs text-ink-2 dark:text-ink-dark-2">
              <span>1 · {p.escala1}</span>
              <span className="text-right">5 · {p.escala5}</span>
            </div>
          </div>
        )}

        {falta && <Alert variant="warning">{tipo === 'disc' ? LP.falta_marcar_disc : LP.falta_marcar_qa}</Alert>}
        {erroAoSalvar && <Alert variant="danger">{LP.falha_ao_salvar}</Alert>}

        <div className="flex gap-3">
          <Button variant="secondary" size="lg" className="flex-1" disabled={indice === 0 || ocupado} onClick={() => setIndice(indice - 1)} data-testid="teste-voltar">
            {LP.botao_voltar}
          </Button>
          <Button size="lg" className="flex-1" disabled={ocupado} onClick={() => void avancar()} data-testid={ultima ? 'teste-enviar' : 'teste-proximo'}>
            {ultima ? LP.botao_enviar : LP.botao_proximo}
          </Button>
        </div>
        <p className="text-xs text-ink-3">{LP.rodape_salvamento}</p>
      </div>
    </Moldura>
  );
}
